// ABOUTME: Text input component with label, loading state, and success indicator
// ABOUTME: Provides consistent text input styling across the application

import type { ChangeEvent } from "react"
import * as React from "react"
import { useEffect, useState } from "react"
import { useDebouncedCallback } from "use-debounce"
import { CLabel } from "./CLabel"
import { CSpinnerInline } from "./CSpinnerInline"
import { Check } from "lucide-react"
import { cn } from "@shared/lib/utils"

// Base Input component
const InputBase = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-lg border border-gray-300 bg-white px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-ellipsis overflow-hidden",
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  },
)
InputBase.displayName = "InputBase"

interface IProps extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange"> {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void
  value?: string | number
  label?: string
  loading?: boolean
  isRequest?: boolean | null | undefined
  required?: boolean
  isNegativeVal?: boolean
  labelPosition?: string
}

function getLabelPosition(labelPosition?: string) {
  if (labelPosition === "left") {
    return "top-4"
  }

  if (labelPosition === "none") {
    return "top-1"
  }

  return "top-8"
}

export const CTextInput = React.forwardRef<HTMLInputElement, IProps>((props, ref) => {
  const { label, loading, isRequest, required, type, isNegativeVal, labelPosition, className, ...args } = props
  const [isSuccess, setIsSuccess] = useState(false)

  // Stable across renders: use-debounce builds the callback in a useMemo keyed
  // only on wait/options (use-debounce@10.1.1 dist/index.js:1,
  // `D=r.useMemo(…,[x,y,e,T,E,p,R,g,w,u])`), so listing it re-runs nothing.
  const debounced = useDebouncedCallback(() => {
    setIsSuccess(false)
  }, 2000)

  useEffect(() => {
    if (typeof isRequest !== "undefined") {
      setIsSuccess(true)
      debounced()
    }
  }, [isRequest, debounced])

  const labelPos = getLabelPosition(labelPosition)

  return (
    <div className="relative">
      {label && label !== "" && (
        <CLabel className="block mb-1">
          {label} {required && <span className="text-red-700">*</span>}
        </CLabel>
      )}
      <InputBase
        ref={ref}
        onKeyDown={(e) => {
          if (isNegativeVal && type === "number" && e.key === "-") {
            e.preventDefault()
          }
        }}
        type={type}
        className={cn("mb-4 pr-11", className)}
        required={required}
        {...args}
      />
      {loading && (
        <CSpinnerInline
          loading
          size="sm"
          className={`absolute ${labelPos} right-3`}
        />
      )}
      {isSuccess && !loading && (
        <Check className={`h-5 w-5 absolute ${labelPosition === "left" ? "top-4" : "top-8"} right-3 text-green-500`} />
      )}
    </div>
  )
})

CTextInput.displayName = "CTextInput"

// Export base input for reuse in other components
export { InputBase }
